import { useScopedClient } from "@/features/clients/hooks/use-scoped-client";
import { isDemoMode } from "@/features/utils/demo-mode";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { DossiersAPI } from "../api/dossiers";
import type { DossierFile, DossierFileSource } from "../types";
import {
  downloadMockFile,
  listMockFiles,
  removeMockFile,
  renameMockFile,
  uploadMockFile,
} from "./use-dossier-files.mock";

export const dossierKeys = {
  all: ["dossiers"] as const,
  files: (clientId: string, customerId: string) =>
    [...dossierKeys.all, "files", clientId, customerId] as const,
};

/** A contact's files, newest first. */
export function useDossierFiles(
  customerId: string,
  queryOptions?: { enabled?: boolean },
) {
  const { clientId } = useScopedClient();

  return useQuery<{ files: DossierFile[] }>({
    queryKey: dossierKeys.files(clientId, customerId),
    queryFn: () =>
      isDemoMode()
        ? Promise.resolve(listMockFiles(customerId))
        : DossiersAPI.listFiles(clientId, customerId),
    staleTime: 30 * 1000,
    enabled: !!clientId && !!customerId && queryOptions?.enabled !== false,
  });
}

export function useUploadDossierFile(customerId: string) {
  const { clientId } = useScopedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      file,
      source,
    }: {
      file: File;
      source: DossierFileSource;
    }) =>
      isDemoMode()
        ? Promise.resolve(uploadMockFile(customerId, file, source))
        : DossiersAPI.uploadFile(clientId, customerId, file, source),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: dossierKeys.files(clientId, customerId),
      }),
  });
}

export function useRenameDossierFile(customerId: string) {
  const { clientId } = useScopedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ fileId, filename }: { fileId: string; filename: string }) =>
      isDemoMode()
        ? Promise.resolve(renameMockFile(customerId, fileId, filename))
        : DossiersAPI.renameFile(clientId, fileId, filename),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: dossierKeys.files(clientId, customerId),
      }),
  });
}

export function useRemoveDossierFile(customerId: string) {
  const { clientId } = useScopedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (fileId: string) =>
      isDemoMode()
        ? Promise.resolve(removeMockFile(customerId, fileId))
        : DossiersAPI.removeFile(clientId, fileId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: dossierKeys.files(clientId, customerId),
      }),
  });
}

/** Fetch one file's blob — the caller opens or saves it (`features/utils/blob`). */
export function useFetchDossierFile() {
  const { clientId } = useScopedClient();
  return (fileId: string): Promise<Blob> =>
    isDemoMode()
      ? Promise.resolve(downloadMockFile(fileId))
      : DossiersAPI.downloadFile(clientId, fileId);
}

/** What a browser draws in an `<img>`; HEIC and the like keep their icon. */
const THUMBNAIL_TYPE = /^image\/(jpeg|png|webp|gif)$/;

/**
 * Past this the whole file is not worth downloading for a 32px square: there is
 * no thumbnail on the server, so the browser fetches the original.
 */
const THUMBNAIL_MAX_BYTES = 8 * 1024 * 1024;

/**
 * A picture's own bytes as an object URL, for a thumbnail — `undefined` until it
 * arrives, and for anything that is not a small image. The route is
 * authenticated, so an `<img src>` cannot reach it: the blob is fetched like a
 * download and cached with the list (a file's bytes never change under its id).
 */
export function useFileThumbnail(
  file: Pick<DossierFile, "id" | "content_type" | "size">,
): string | undefined {
  const { clientId } = useScopedClient();
  const fetchFile = useFetchDossierFile();
  const eligible =
    THUMBNAIL_TYPE.test(file.content_type) && file.size <= THUMBNAIL_MAX_BYTES;

  const { data: blob } = useQuery<Blob>({
    queryKey: [...dossierKeys.all, "blob", clientId, file.id],
    queryFn: () => fetchFile(file.id),
    enabled: !!clientId && eligible,
    staleTime: Infinity,
    gcTime: 5 * 60 * 1000,
    retry: false,
  });

  const [url, setUrl] = useState<string>();
  useEffect(() => {
    if (!blob) return setUrl(undefined);
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);

  return url;
}
